/**
 * «Создать кабинет продавца» по заявке.
 *
 * До этого заявка и аккаунт жили порознь: менеджер мог довести карточку до
 * «Активен», за которым не стояло ни продавца, ни магазина, а связывались они,
 * только если бизнес регистрировался сам. Здесь менеджер заводит кабинет по
 * телефону из заявки: почта нужна для входа, ИНН и название подставляются.
 * Пароль не придумываем и не высылаем — продавцу уходит одноразовая ссылка.
 *
 * Раньше блок жил в модалке заявки; теперь его открывает карточка CRM.
 */
import { Button } from "@osmix/ui/button";
import { Input } from "@osmix/ui/input";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { api, type ApplicationAccountResult, type SellerApplication } from "@/api";
import { applicationKeys } from "@/api/hooks/use-applications";
export function CreateAccountBlock({
  application,
  defaultOpen = false,
  onCancel,
  onCreated
}: {
  application: SellerApplication;
  /** Сразу форма, без кнопки «Создать кабинет» (когда блок открыт в диалоге). */
  defaultOpen?: boolean;
  onCancel?: () => void;
  onCreated?: (result: ApplicationAccountResult) => void;
}) {
  const {
    t
  } = useTranslation();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(defaultOpen);
  const [email, setEmail] = useState(application.email ?? "");
  const [shopName, setShopName] = useState(application.shopName ?? "");
  const [inn, setInn] = useState(application.inn ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<ApplicationAccountResult | null>(null);
  if (application.userId && !result) {
    return <p className="rounded-md border bg-muted/40 p-3 text-sm text-muted-foreground">
        {t("applications.account.exists", {
        defaultValue: "Кабинет продавца уже заведён — заявка связана с аккаунтом."
      })}
      </p>;
  }
  if (result) {
    return <div className="flex flex-col gap-2 rounded-md border border-emerald-500/40 bg-emerald-500/10 p-3 text-sm">
        <p className="font-medium text-emerald-700 dark:text-emerald-400">
          {t("applications.account.created", {
          defaultValue: "Кабинет создан. Продавец задаёт пароль по ссылке:"
        })}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <code className="break-all rounded bg-background px-2 py-1 text-xs">{result.inviteUrl}</code>
          <Button size="sm" variant="outline" onClick={() => void navigator.clipboard.writeText(result.inviteUrl)}>
            {t("common.copy", {
            defaultValue: "Скопировать"
          })}
          </Button>
        </div>
        <p className="text-xs text-muted-foreground">
          {result.smsSent ? t("applications.account.smsSent", {
          defaultValue: "Ссылка отправлена в SMS."
        }) : t("applications.account.smsFailed", {
          defaultValue: "SMS не ушла — отправьте ссылку сами."
        })}
        </p>
      </div>;
  }
  if (!open) {
    return <Button variant="outline" size="sm" className="self-start" onClick={() => setOpen(true)}>
        {t("applications.account.action", {
        defaultValue: "Создать кабинет продавца"
      })}
      </Button>;
  }
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const created = await api.applications.createAccount(application.id, {
        email: email.trim(),
        shopName: shopName.trim() || undefined,
        inn: inn.trim() || undefined
      });
      setResult(created);
      onCreated?.(created);
      await queryClient.invalidateQueries({
        queryKey: applicationKeys.board
      });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  };
  return <div className="flex flex-col gap-2 rounded-md border p-3">
      <p className="text-sm font-medium">
        {t("applications.account.action", {
        defaultValue: "Создать кабинет продавца"
      })}
      </p>
      <p className="text-xs text-muted-foreground">
        {t("applications.account.hint", {
        defaultValue: "Почта нужна для входа в кабинет. Пароль продавец задаёт сам по ссылке — мы его не высылаем."
      })}
      </p>
      <div className="grid gap-2 sm:grid-cols-3">
        <Input placeholder={t("applications.account.email", {
        defaultValue: "Почта продавца"
      })} aria-label="Почта продавца" value={email} onChange={e_0 => setEmail(e_0.target.value)} />
        <Input placeholder={t("applications.account.shop", {
        defaultValue: "Название магазина"
      })} aria-label="Название магазина" value={shopName} onChange={e_1 => setShopName(e_1.target.value)} />
        <Input placeholder={t("applications.inn", {
        defaultValue: "ИНН"
      })} aria-label="ИНН" value={inn} onChange={e_2 => setInn(e_2.target.value)} />
      </div>
      {error && <p className="text-sm text-destructive">{error}</p>}
      <div className="flex items-center gap-2">
        <Button size="sm" disabled={busy || email.trim().length < 3} onClick={() => void submit()}>
          {t("applications.account.submit", {
          defaultValue: "Создать и отправить ссылку"
        })}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={() => {
        setOpen(false);
        onCancel?.();
      }}>
          {t("common.cancel", {
          defaultValue: "Отмена"
        })}
        </Button>
      </div>
    </div>;
}